import { ApolloClient, NormalizedCacheObject } from 'apollo-boost'

import { getRrp } from '@thg-commerce/enterprise-pricing/src'

import { Country, Currency } from '../../../../generated/graphql'
import { ClientBehaviours } from '../../../utils'
import { Basket as query } from '../../../../graphql/Query/Basket/Basket.graphql'
import {
  normaliseBasket,
  NormalisedBasket,
} from '../../../../transformers/basket'
import { DescriptionKeys } from '../../../../transformers/product/description'
import { getColourSwatches } from '../../../../transformers/product/colorSwatch'
import { Configuration } from '../../../../../types'

export type BasketData = NormalisedBasket

export interface BasketVariables {
  id?: string
  shippingDestination: Country
  currency: Currency
  productDescriptionKeys?: DescriptionKeys
  productContentKeys?: string[]
  wishlistEnabled?: boolean
  loyaltyEnabled?: boolean
  clickAndCollectEnabled?: boolean
  keys?: string[]
  subscriptionsEnabled?: boolean
  subscriptionContractsEnabled?: boolean
  vipPriceEnabled?: boolean
}

export const Basket = (
  client: ApolloClient<NormalizedCacheObject>,
  { behaviours }: { behaviours: ClientBehaviours },
  config: Configuration['publicRuntimeConfig'],
) => async (_: any, args: BasketVariables): Promise<BasketData> => {
  if (behaviours.override) {
    return await import('../../../data/Basket').then((mod) => {
      return mod.BASKET_DATA as BasketData
    })
  }
  const { data } = await client.query({
    query,
    variables: args,
  })

  const appliedOffers = config.siteConfig.displayFreeGiftSavingMessage
    ? data.basket.appliedOffers
    : data.basket.appliedOffers?.filter(
        (offer) =>
          !data.basket.selectYourSample?.filter(
            (sample) => sample.message === offer.message,
          ).length,
      )

  const basketDataWithPrice = {
    ...data.basket,
    appliedOffers,
    items: data.basket.items.map((item) => {
      return {
        ...item,
        product: {
          ...item.product,
          product: {
            ...item.product.product,
            basketImage:
              item.product.product.variants?.find(
                (variant) => variant.sku === item.product.sku,
              )?.images?.[0]?.largeProduct ||
              item.product.product?.images?.[0]?.largeProduct ||
              '',
          },
        },
      }
    }),
    recommendations: data.basket.recommendations.map((item) => {
      const colourSwatches = getColourSwatches(item)

      const productPrice =
        item.defaultVariant?.price ||
        item.cheapestVariant?.price ||
        item.variants?.[0]?.price

      return {
        ...item,
        colourSwatches: colourSwatches ?? null,
        price: productPrice?.price.displayValue
          ? {
              price: {
                defaultPrice: productPrice.price.displayValue,
                minPrice:
                  item.cheapestVariant?.price?.price.displayValue || undefined,
                maxPrice:
                  item.mostExpensiveVariant?.price?.price.displayValue ||
                  undefined,
              },
              rrp: {
                defaultPrice: getRrp(productPrice),
                minPrice:
                  item.cheapestVariant?.price &&
                  getRrp(item.cheapestVariant?.price),
                maxPrice:
                  item.mostExpensiveVariant?.price &&
                  getRrp(item.mostExpensiveVariant?.price),
              },
            }
          : undefined,
        inStock: item.variants.some((variant) => variant.inStock),
      }
    }),
  }

  return normaliseBasket(
    basketDataWithPrice,
    args.productDescriptionKeys,
    config.siteConfig.qubit?.enabled,
  )
}
